import { Field } from '@/components/commons';
import { ImageService, SUPPORTED_FORMATS } from '@/services/image';
import { UploadService } from '@/services/upload';
import { NOTIFY_STATUS, notificationMessage } from '@/utils';
import {
  validateVideoFile,
  VIDEO_FILE_SIZE_LIMIT,
  VideoValidationProfile,
} from '@/utils/videoValidation';
import { CloseOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface IProps {
  /** 動画 URL を入れるフォーム項目名 */
  name: string;
  /** ポスター画像 URL を入れるフォーム項目名 */
  posterName: string;
  /** 解析結果の置き場所。指定した項目だけ書き込む */
  durationName?: string;
  widthName?: string;
  heightName?: string;
  label?: string;
  /** 表示面ごとの入稿条件（尺・比率） */
  profile: VideoValidationProfile;
  /**
   * URL の直接入力を許すか。
   * 既に外部に置いてある動画を指しているデータがあるうちは残す必要がある。
   */
  allowUrlInput?: boolean;
  /**
   * 動画・ポスターのアップロード中かどうか。親の保存ボタンを止めるのに使う。
   * 途中で保存すると URL が空のまま、あるいはポスターなしで確定してしまう。
   */
  onUploadingChange?: (isUploading: boolean) => void;
}

/**
 * 動画の入稿欄。アップロードすると faststart や コーデックをその場で検査し、
 * 1 フレーム目からポスター画像を作って一緒に上げる。
 *
 * ここでの検査をすり抜けてもサーバー（POST /admin/uploads/video）が実バイトで
 * 弾くので、目的は「100MB 送ってから怒られる」のを避けることと原因を示すこと。
 */
export const VideoUploadField: React.FC<IProps> = ({
  name,
  posterName,
  durationName,
  widthName,
  heightName,
  label = '動画',
  profile,
  allowUrlInput = true,
  onUploadingChange,
}) => {
  const { watch, setValue, setError, clearErrors } = useFormContext();
  // 動画とポスターの手動差し替えは並行し得るので件数で数える
  const [pendingUploads, setPendingUploads] = useState(0);
  const [warnings, setWarnings] = useState<string[]>([]);

  const isUploading = pendingUploads > 0;

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const startUpload = () => setPendingUploads((count) => count + 1);
  const finishUpload = () => setPendingUploads((count) => count - 1);

  const videoUrl: string | undefined = watch(name);
  const posterUrl: string | undefined = watch(posterName);

  const setMeta = (field: string | undefined, value: number | null) => {
    if (field) setValue(field, value);
  };

  /** 前の動画のポスターと寸法。別の動画に変わったら残してはいけない */
  const clearVideoMeta = () => {
    setValue(posterName, '', { shouldDirty: true });
    setMeta(durationName, null);
    setMeta(widthName, null);
    setMeta(heightName, null);
  };

  const clearVideo = () => {
    // 空文字を送ることでサーバー側が解除と解釈する。undefined だと
    // 「触らない」扱いになり、いつまでも消せない。
    setValue(name, '', { shouldValidate: true, shouldDirty: true });
    clearVideoMeta();
    setWarnings([]);
  };

  const handleUrlInput = (value: string) => {
    setValue(name, value, { shouldValidate: true, shouldDirty: true });
    clearVideoMeta();
    setWarnings([]);
  };

  const handleUpload = async (file: File) => {
    startUpload();
    setWarnings([]);

    try {
      const validation = await validateVideoFile(file, profile);

      if (validation.errors.length > 0) {
        setError(name, {
          type: 'validate',
          message: validation.errors.join('\n'),
        });

        return;
      }

      clearErrors(name);
      setWarnings(validation.warnings);

      const res = await UploadService.uploadVideo(file);

      clearVideoMeta();
      setValue(name, res?.data?.url ?? '', {
        shouldValidate: true,
        shouldDirty: true,
      });
      setMeta(durationName, res?.data?.durationSec ?? null);
      setMeta(widthName, res?.data?.width ?? null);
      setMeta(heightName, res?.data?.height ?? null);

      // ポスターは運用者に用意させると忘れられるうえ、1 フレーム目以外を
      // 選ばれると再生開始の瞬間に絵が飛ぶ。自動生成に寄せる。
      if (validation.posterFile) {
        const poster = await ImageService.uploadFile(validation.posterFile);
        setValue(posterName, poster?.data?.url ?? '', { shouldDirty: true });
      }
    } catch (error) {
      setError(name, {
        type: 'validate',
        message:
          (error as { message?: string })?.message ??
          '動画のアップロードに失敗しました。もう一度お試しください。',
      });
    } finally {
      finishUpload();
    }
  };

  const beforeUploadVideo = (file: File) => {
    // 並行して上げると後に返った方で URL とポスターが食い違う
    if (isUploading) return Upload.LIST_IGNORE;

    if (file.size > VIDEO_FILE_SIZE_LIMIT) {
      setError(name, {
        type: 'validate',
        message: '動画のサイズは100MBを超えています。',
      });

      return Upload.LIST_IGNORE;
    }

    handleUpload(file);

    return true;
  };

  const beforeUploadPoster = (file: File) => {
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      setError(posterName, {
        type: 'validate',
        message: 'jpg / jpeg / png のみアップロードできます。',
      });

      return Upload.LIST_IGNORE;
    }

    clearErrors(posterName);
    startUpload();
    ImageService.uploadFile(file)
      .then((res) =>
        setValue(posterName, res?.data?.url ?? '', { shouldDirty: true }),
      )
      .catch(() =>
        notificationMessage({
          message: 'ポスター画像のアップロードに失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        }),
      )
      .finally(finishUpload);

    return true;
  };

  return (
    <div className='mb-6'>
      <label className='title-calendar font-semibold'>{label}</label>

      <div className='pt-2'>
        {!videoUrl ? (
          <Upload
            beforeUpload={beforeUploadVideo}
            disabled={isUploading}
            customRequest={() => {}}
            accept='video/mp4'
            listType='text'
            maxCount={1}
            showUploadList={false}
          >
            <Button
              type='default'
              loading={isUploading}
              className='text-[#84ab42] border border-[#84ab42] font-semibold'
            >
              動画を選択
            </Button>
          </Upload>
        ) : (
          <div className='relative inline-block'>
            <video
              src={videoUrl}
              poster={posterUrl || undefined}
              controls
              playsInline
              preload='metadata'
              className='block rounded-lg bg-black'
              style={{
                width: 320,
                aspectRatio: `${profile.aspectRatio}`,
              }}
            />
            <Button
              type='primary'
              shape='circle'
              icon={<CloseOutlined />}
              size='small'
              disabled={isUploading}
              onClick={clearVideo}
              className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
            />
          </div>
        )}
      </div>

      {/* 検査・アップロード・URL 入力のエラー表示用 */}
      <Field name={name} className='mt-[-8px]'>
        <></>
      </Field>

      <div className='mb-2 text-sm text-gray-500'>
        推奨: {profile.recommendedResolution.width}×
        {profile.recommendedResolution.height}（{profile.aspectRatioLabel}）/
        H.264 / {profile.recommendedMaxDurationSec}秒以内 / faststart 有効
      </div>

      {warnings.length > 0 && (
        <Alert
          type='warning'
          showIcon
          className='mb-3'
          message='確認してください'
          description={
            <ul className='m-0 pl-4'>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
        />
      )}

      {videoUrl && (
        <div className='mb-3'>
          <div className='text-sm font-semibold mb-1'>
            ポスター画像（再生開始までの表示）
          </div>
          {posterUrl ? (
            <div className='relative inline-block'>
              <img
                src={posterUrl}
                alt=''
                className='block rounded'
                style={{ width: 160 }}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                disabled={isUploading}
                onClick={() => setValue(posterName, '', { shouldDirty: true })}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            </div>
          ) : (
            <Upload
              beforeUpload={beforeUploadPoster}
              disabled={isUploading}
              customRequest={() => {}}
              accept='image/jpeg,image/png'
              listType='text'
              maxCount={1}
              showUploadList={false}
            >
              <Button type='default' size='small' loading={isUploading}>
                画像を選択
              </Button>
            </Upload>
          )}
          <Field name={posterName} className='mt-[-8px]'>
            <></>
          </Field>
        </div>
      )}

      {allowUrlInput && (
        <div>
          <div className='text-sm text-gray-500 mb-1'>
            外部に置いた動画を指す場合は URL を直接入力します（mp4
            の直リンクのみ）
          </div>
          {/*
            Field に Input を渡すと値の変更を横取りできないため、直接つなぐ。
            URL を書き換えたら前の動画のポスターと寸法は別物なので外す。
          */}
          <Input
            placeholder='動画URLを入力'
            maxLength={500}
            value={videoUrl ?? ''}
            disabled={isUploading}
            onChange={(e) => handleUrlInput(e.target.value)}
          />
        </div>
      )}
    </div>
  );
};
